<template>
  <div class="subcard">
    <div class="subcard-head">
      <h6 class="subcard-title">{{ title }}</h6>
    </div>
    <div class="subcard-body">

      <!-- Movement Pattern -->
      <div class="block">
        <label class="form-label">{{ $t('zoneSettings.direction.movementPattern') }}</label>
        <p class="field-help">{{ $t('zoneSettings.direction.movementPatternHelp') }}</p>
        <div class="radio-row">
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`pattern-${uid}`"
              :id="`${uid}-pattern-vertical`"
              value="vertical"
              v-model="direction.movementPattern"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.vertical') }}</span>
          </label>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`pattern-${uid}`"
              :id="`${uid}-pattern-horizontal`"
              value="horizontal"
              v-model="direction.movementPattern"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.horizontal') }}</span>
          </label>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`pattern-${uid}`"
              :id="`${uid}-pattern-diagonal`"
              value="diagonal"
              v-model="direction.movementPattern"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.diagonal') }}</span>
          </label>
        </div>
      </div>

      <!-- Direction descriptor -->
      <div class="block">
        <label class="form-label">{{ pickerLabel }}</label>
        <p class="field-help">{{ pickerTooltip }}</p>

        <div class="radio-line" v-if="direction.movementPattern !== 'horizontal'">
          <i class="bi bi-arrow-down-up icon-muted"></i><span class="sep">:</span>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`vert-${uid}`"
              :id="`${uid}-up`"
              value="up"
              v-model="direction.vertical"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.up') }}</span>
          </label>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`vert-${uid}`"
              :id="`${uid}-down`"
              value="down"
              v-model="direction.vertical"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.down') }}</span>
          </label>
        </div>

        <div class="radio-line" v-if="direction.movementPattern !== 'vertical'">
          <i class="bi bi-arrow-left-right icon-muted"></i><span class="sep">:</span>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`horz-${uid}`"
              :id="`${uid}-left`"
              value="left"
              v-model="direction.horizontal"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.rightToLeft') }}</span>
          </label>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`horz-${uid}`"
              :id="`${uid}-right`"
              value="right"
              v-model="direction.horizontal"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.leftToRight') }}</span>
          </label>
        </div>
      </div>

      <!-- Anchor point — tripwires only (polygon-level direction has no
           bounding-box anchor, so showAnchor is left false for that case). -->
      <div class="block" v-if="showAnchor">
        <label class="form-label">{{ $t('zoneSettings.direction.anchor') }}</label>
        <p class="field-help">{{ $t('zoneSettings.direction.anchorHelp') }}</p>
        <div class="radio-row">
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`anchor-${uid}`"
              :id="`${uid}-anchor-foot`"
              value="foot"
              v-model="direction.anchor"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.feet') }}</span>
          </label>
          <label class="form-check form-check-inline">
            <input
              type="radio"
              class="form-check-input"
              :name="`anchor-${uid}`"
              :id="`${uid}-anchor-center`"
              value="center"
              v-model="direction.anchor"
            />
            <span class="form-check-label">{{ $t('zoneSettings.direction.center') }}</span>
          </label>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
    name: 'DirectionPicker',

    props: {
        // The reactive direction object. The child mutates its properties
        // directly via v-model; the parent's reactive proxy observes the
        // updates so the binding is two-way without an explicit emit.
        direction: { type: Object, required: true },
        // Unique key used to namespace radio-group names / input ids so multiple
        // pickers on the same modal don't share groups. Typically the tripwire
        // id, or a literal like "polygon" for the zone-level direction.
        uid: { type: String, required: true },
        // Card title — e.g. "Tripwire 1 — Direction of Store Entry".
        title: { type: String, required: true },
        // Label and tooltip for the direction descriptor row.
        pickerLabel: { type: String, required: true },
        pickerTooltip: { type: String, default: '' },
        // When true, render the per-tripwire anchor-point radio. Polygon-level
        // direction has no bounding-box anchor concept, so callers pass false
        // (the default) for the Combined-EE polygon row.
        showAnchor: { type: Boolean, default: false },
    },
}
</script>

<style scoped>
/* Subcard styling must be replicated here because the parent component's
   <style scoped> block does not bleed into child-component DOM. Keep these
   rules in sync with the .subcard / .subcard-head / .subcard-title /
   .subcard-body / .form-label / .field-help rules in DetectionZoneSettings.vue. */
.subcard{
  border:1px solid #e6edf3;
  border-radius:12px;
  margin-top:24px;
  overflow:hidden;
  box-shadow:0 2px 8px rgba(13,38,76,.05);
}
.subcard-head{
  padding:10px 12px;
  border-bottom:1px solid #eef3f7;
  background:#f9fbfd;
}
.subcard-title{ margin:0; color:#4F7EB3; font-weight:800 }
.subcard-body{ padding:12px 12px }

.form-label{
  display:block;
  margin-bottom:.25rem;
  font-weight:700;
  color:#425461;
}
.field-help{
  margin:-2px 0 8px 0;
  font-size:.78rem;
  line-height:1.35;
  color:#8a99a4;
  font-weight:400;
}
.icon-muted{ color:#7a8a96 }
.sep{ margin: 0 6px; color:#7a8a96 }
.radio-row{ display:flex; gap:18px; flex-wrap:wrap }
.radio-line{ display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-top:6px }
.block{ margin: 10px 0 14px }
</style>
